<template>
  <div
    ref="navbarNode"
    class="nav"
  >
    <template v-if="!sightsStore.navIsLoading">
      <div
        v-for="navEl in navRes"
        :key="navEl.dataset"
        :class="['nav-el', `nav-el-${navEl.dataset}`, { 'nav-el-active': dataSet === navEl.dataset }]"
        @click="goTo(navEl.dataset)"
      >
        {{ navEl.title }}
      </div>
    </template>
    <template v-else>
      <SkeletonItem
        v-for="el in 3"
        :key="el"
        class="skeleton"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
  import { SkeletonItem } from '@experience/core-skeleton-lib';
  import { onMounted, nextTick, ref, onDeactivated, useTemplateRef, watch } from 'vue';

  import { trackEvent } from '@/services/analytics';
  import { useSightsStore } from '@/shared/store/sights';
  import { useSightAnalyticsParams } from '@/shared/utils/analytics';

  const navbar = useTemplateRef('navbarNode');
  const emit = defineEmits(['useNavbarTab']);

  const sightsStore = useSightsStore();
  const navRes = ref<NavItem[]>([]);
  const analyticsParams = useSightAnalyticsParams(() => sightsStore.activeSight);

  interface NavItem {
    dataset: string;
    title: string;
  }

  const nav: NavItem[] = [
    { dataset: 'info', title: 'Обзор' },
    { dataset: 'position', title: 'Расположение' },
    { dataset: 'guides', title: 'Гиды рассказывают' },
    { dataset: 'experience', title: 'Экскурсии' },
  ];

  const getRoot = (): Document | ShadowRoot | undefined =>
    navbar.value?.getRootNode() as Document | ShadowRoot | undefined;

  const newNav = () => {
    navRes.value = [];
    Object.keys(sightsStore.modalNav).forEach((el) => {
      if (sightsStore.modalNav[el as keyof (typeof sightsStore)['modalNav']]) {
        const findEl = nav.find((el2) => el2.dataset === el);

        if (findEl) {
          navRes.value.push(findEl);
        }
      }
    });
  };

  onMounted(() => {
    setTimeout(createObs, 1000);
  });

  onDeactivated(() => {
    unObserve();
  });

  const nodes = ref<NodeListOf<HTMLElement> | null>(null);
  const nodeExp = ref<HTMLElement | null>(null);
  const dataSet = ref<string>('info');
  const observer = ref<IntersectionObserver | null>(null);
  const observerExp = ref<IntersectionObserver | null>(null);

  const initObs = (options: IntersectionObserverInit): IntersectionObserver => {
    return new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry: IntersectionObserverEntry) => {
        if (entry.isIntersecting) {
          dataSet.value = (entry.target as HTMLElement).dataset.attr as string;
          const nodeToScroll = getRoot()?.querySelector<HTMLElement>(`.nav-el-${dataSet.value}`);

          if (navbar.value) {
            navbar.value.scrollLeft = nodeToScroll?.offsetLeft ?? 0;
          }
        }
      });
    }, options);
  };

  const createObs = (): void => {
    void nextTick(() => {
      unObserve();
      observer.value?.disconnect();

      const root = getRoot();
      const scrollContainer = root?.querySelector<HTMLElement>('.ui-modal__scroll-container');

      nodes.value = root?.querySelectorAll<HTMLElement>('.obs-el') ?? null;

      const options: IntersectionObserverInit = {
        root: scrollContainer ?? null,
        rootMargin: '0px',
        threshold: 1,
      };

      observer.value = initObs(options);

      nodes.value?.forEach((i: HTMLElement) => {
        observer.value?.observe(i);
      });
    });
  };

  const unObserve = (): void => {
    if (nodes.value) {
      nodes.value.forEach((i: HTMLElement) => {
        if (observer.value) {
          observer.value.unobserve(i);
        }
      });
    }

    if (nodeExp.value && observerExp.value) {
      observerExp.value.unobserve(nodeExp.value);
    }
  };

  const goTo = (id: string): void => {
    const targetElement = getRoot()?.querySelector<HTMLElement>(`#${id}`);

    emit('useNavbarTab', id);
    void trackEvent('attractions:sights:sight-popup.tab-click', { tab_name: id, ...analyticsParams.value });
    dataSet.value = id;
    targetElement?.scrollIntoView({ behavior: 'smooth' });
  };

  watch(
    sightsStore.modalNav,
    () => {
      newNav();
      createObs();
    },
    { deep: true, immediate: true },
  );
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as legacy-typography;

  .nav {
    display: flex;
    padding: 0 var(--alias-space-8);
    overflow-x: scroll;
    scrollbar-width: none;
    background: var(--alias-color-background-default);
    border-bottom: 1px solid var(--alias-color-border-default);
    -ms-overflow-style: none;
  }

  .nav-el {
    padding: var(--alias-space-12) var(--alias-space-8);
    color: var(--alias-color-text-subtle);
    white-space: nowrap;
    cursor: pointer;

    @include legacy-typography.base-400-300-regular;
  }

  .nav-el-active {
    color: var(--alias-color-text-default);
    border-bottom: 2px solid var(--alias-color-border-focused);
  }

  .skeleton {
    width: 100px;
    height: 22px;
    margin: var(--alias-space-12) var(--alias-space-8);
    border-radius: var(--alias-radius-8);
  }
</style>
